// Lista ofert OKRESOWYCH klienta (SalesCRM, is_periodic=true).
//
// Sortowanie: na gorze oferty wciaz wazne (validUntil >= dzis), na dole te wygasle.
// Wewnatrz kazdej grupy: aktywne sortowane po validUntil ASC (najblizej konca jako pierwsze
// - klient widzi co najpilniejsze), wygasle sortowane po validUntil DESC (najnowsza
// wygasla na gorze). Sortowanie po stronie frontu w obrebie aktualnie zaladowanej strony.

import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { CircularProgress } from '@mui/material';

import { reduxActions, useDispatch } from 'store';
import { useGetSalesCrmMyOffers } from 'api';
import type { ISalesCrmMyOfferListItem } from 'api/endpoints/useGetSalesCrmMyOffers';
import { OfferStatusBadge, PageTitle, Pagination, SearchInput } from 'components/controls';

import styles from 'theme/pages/OffersPeriodic/OffersPeriodic.module.scss';

const formatDate = (raw: string | null): string => {
  if (!raw) return '-';
  const isoDate = raw.length >= 10 ? raw.substring(0, 10) : raw;
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  }
  return `${match[3]}.${match[2]}.${match[1]}`;
};

const validUntilTs = (raw: string | null): number | null => {
  if (!raw) return null;
  const isoDate = raw.length >= 10 ? raw.substring(0, 10) : raw;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  return d.getTime();
};

const OffersPeriodic: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 20,
    search_keyword: '',
    is_periodic: true
  });

  const { data, isLoading, isError } = useGetSalesCrmMyOffers(queryParams);

  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs([
        { name: t('Dashboard'), path: '/dashboard' },
        { name: t('Oferty okresowe') }
      ])
    );
  }, []);

  const items = data?.items ?? [];
  const totalPages = data?.total_pages ?? 1;
  const totalCount = data?.total_count ?? 0;

  const todayStartTs = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, []);

  // Podzial na aktywne / wygasle. Bez valid_until = "aktywna bezterminowo" -> grupa aktywnych.
  const sortedItems = useMemo<ISalesCrmMyOfferListItem[]>(() => {
    const active: ISalesCrmMyOfferListItem[] = [];
    const expired: ISalesCrmMyOfferListItem[] = [];

    for (const offer of items) {
      const ts = validUntilTs(offer.valid_until);
      if (ts === null || ts >= todayStartTs) {
        active.push(offer);
      } else {
        expired.push(offer);
      }
    }

    // Aktywne ASC po validUntil - najpilniejsze na gorze; brak validUntil na koncu aktywnych.
    active.sort((a, b) => {
      const tsA = validUntilTs(a.valid_until);
      const tsB = validUntilTs(b.valid_until);
      if (tsA === null && tsB === null) return 0;
      if (tsA === null) return 1;
      if (tsB === null) return -1;
      return tsA - tsB;
    });

    // Wygasle DESC po validUntil - najnowsza wygasla na gorze sekcji.
    expired.sort((a, b) => (validUntilTs(b.valid_until) ?? 0) - (validUntilTs(a.valid_until) ?? 0));

    return [...active, ...expired];
  }, [items, todayStartTs]);

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-OffersPeriodic')}>
      <PageTitle
        title={
          <>
            <Trans>Oferty okresowe</Trans>{' '}
            <span className={styles.thin}>({totalCount})</span>
          </>
        }
      />

      <div className={styles.header}>
        <div className={styles.searchFormWrapper}>
          <SearchInput
            placeholder={`${t('Szukaj po numerze oferty')}...`}
            onChange={(search_keyword) =>
              setQueryParams((prev) => ({ ...prev, page: 1, search_keyword }))
            }
          />
        </div>
      </div>

      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress size={28} />
        </div>
      )}

      {!isLoading && isError && (
        <div style={{ padding: 24, color: '#b3142d' }}>
          {t('Nie udało się pobrać ofert. Spróbuj ponownie później.')}
        </div>
      )}

      {!isLoading && !isError && sortedItems.length === 0 && (
        <div style={{ padding: 24, color: '#4b556a' }}>
          {t('Brak ofert do wyświetlenia.')}
        </div>
      )}

      {!isLoading && !isError && sortedItems.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f6fa', textAlign: 'left' }}>
                <th style={{ padding: '12px 16px' }}>{t('Numer oferty')}</th>
                <th style={{ padding: '12px 16px' }}>{t('Status')}</th>
                <th style={{ padding: '12px 16px' }}>{t('Data utworzenia')}</th>
                <th style={{ padding: '12px 16px' }}>{t('Ważna do')}</th>
                <th style={{ padding: '12px 16px', textAlign: 'right' }}>{t('Akcja')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((offer) => {
                const ts = validUntilTs(offer.valid_until);
                const isExpired = ts !== null && ts < todayStartTs;
                return (
                  <tr
                    key={offer.id}
                    style={{
                      borderBottom: '1px solid #eef0f4',
                      color: isExpired ? '#9ca3af' : undefined,
                      backgroundColor: isExpired ? '#fafafa' : undefined
                    }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600 }}>
                      {offer.number ?? `#${offer.id}`}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <OfferStatusBadge status={offer.status} label={offer.status_formatted} />
                    </td>
                    <td style={{ padding: '12px 16px', color: '#4b556a' }}>
                      {formatDate(offer.sys_insert_date)}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {formatDate(offer.valid_until)}
                      {isExpired && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            color: '#b3142d',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                          }}>
                          {t('Wygasła')}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/offers/dedicated/${offer.id}`)}
                        style={{
                          padding: '6px 16px',
                          borderRadius: 6,
                          border: '1px solid #6b54ff',
                          background: '#fff',
                          color: '#6b54ff',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}>
                        {t('Zobacz')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        pagesCount={totalPages}
        page={queryParams.page}
        onChange={(page) => setQueryParams((prev) => ({ ...prev, page }))}
      />
    </div>
  );
};

export default OffersPeriodic;
